import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { defer, EMPTY, from, of } from 'rxjs';
import { catchError, concatMap, exhaustMap, filter, finalize, map, tap, withLatestFrom } from 'rxjs/operators';
import { CachedPayload, CachedPOSTRequest, CACHED_REQUEST_STATUS } from '../../classes/cached-request';
import { OfflineService } from '../../service/offline.service';
import { HttpUtilities } from '../../util/http-utilities';
import { removeRequestFromQueue, synchronizeQueue, updateRequestInQueue } from '../actions/request-queue.actions';
import { selectRequestQueue } from '../selectors/request-queue.selector';
import { PaceOfflineFeatureState } from '../state';
import { OfflinePluginDoubleCacheError } from '../../classes/pace-offline.errors';

@Injectable()
export class RequestQueueEffects {
    public constructor(
        private action$: Actions,
        private store: Store<PaceOfflineFeatureState>,
        private httpClient: HttpClient,
        private offlineService: OfflineService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
    ) {}

    public syncRequestQueue$ = createEffect(() =>
        this.action$.pipe(
            ofType(synchronizeQueue),
            withLatestFrom(this.store.select(selectRequestQueue)),
            filter(([, requests]) => requests.length > 0),
            // exhaust map to prevent order of request processing
            exhaustMap(([, requests]) =>
                defer(() => {
                    this.offlineService.setSyncing(true);
                    return from(requests);
                }).pipe(
                    concatMap((request: CachedPOSTRequest) => {
                        this.log.debug(`RequestQueueEffects: syncRequestQueue$: processing request.id: ${request.id}`);
                        const headers = HttpUtilities.createHttpHeader(request.headers);
                        this.log.debug(
                            `RequestQueueEffects: syncRequestQueue$: url: ${request.url}, endpoint: ${
                                request.url.toLowerCase().indexOf('deepinsert') > 0
                                    ? (<CachedPayload>request.body)?.Endpoint
                                    : request.url
                            }`,
                        );
                        return this.httpClient.post(request.url, request.body, { headers }).pipe(
                            tap(() =>
                                this.log.debug(
                                    `RequestQueueEffects: syncRequestQueue$: removing request.id: ${request.id}`,
                                ),
                            ),
                            map(() => removeRequestFromQueue({ request: request })),
                            catchError((e) => {
                                if (e instanceof OfflinePluginDoubleCacheError) {
                                    this.log.debug(
                                        `RequestQueueEffects: sent a cached request while still offline. This should actually not happen! But isn't too bad either...`,
                                    );
                                    return EMPTY;
                                }

                                this.log.error(
                                    `RequestQueueEffects: syncRequestQueue$: ${request.url} and ${
                                        request.url.toLowerCase().indexOf('deepinsert') > 0
                                            ? (<CachedPayload>request.body)?.Endpoint
                                            : request.url
                                    }: ${JSON.stringify(e, Object.getOwnPropertyNames(e))}`,
                                );
                                return of(updateRequestInQueue({ requestStatus: CACHED_REQUEST_STATUS.ERROR }));
                            }),
                        );
                    }),
                    catchError((error) => {
                        this.log.error(
                            `RequestQueueEffects: syncRequestQueue$: error: ${JSON.stringify(
                                error,
                                Object.getOwnPropertyNames(error),
                            )}`,
                        );
                        return EMPTY;
                    }),
                    finalize(() => this.offlineService.setSyncing(false)),
                ),
            ),
        ),
    );
}
